import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const stats = [
  { value: "1,536", label: "Acts tracked" },
  { value: "69,233", label: "Compliance tasks a year" },
  { value: "6,618", label: "Filings every year" },
];

const values = [
  {
    icon: "⚖️",
    name: "Integrity",
    desc: "Compliance is trust wearing a technical name.",
  },
  {
    icon: "💡",
    name: "Innovation",
    desc: "\"We've always done it this way\" is how backlogs happen.",
  },
  {
    icon: "🤝",
    name: "Customer Focus",
    desc: "We build for the person actually doing the filing, not just the person signing the contract.",
  },
  {
    icon: "🏆",
    name: "Excellence",
    desc: "In this line of work, mostly right isn't good enough.",
  },
  {
    icon: "🎯",
    name: "Specificity",
    desc: "Generic advice doesn't survive contact with a state-specific rule.",
  },
];

const products = [
  {
    num: "1",
    title: "H-Square",
    desc: "Weeks of manual UAN, IP and bank-statement reconciliation, site by site, finished in under 45 minutes.",
  },
  {
    num: "2",
    title: "R-Cube",
    desc: "CLRA registers and returns automated across 1,600+ locations — audit-ready reports in under 40 minutes, without adding a single person to your team.",
  },
];

const Articles = () => {
  return (
    <>
      <article className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-[#d97925] uppercase tracking-widest font-semibold text-sm mb-3">
            From the Founder
          </p>

          <h1 className="text-5xl font-bold tracking-tight leading-tight mb-10">
            Why I Started TickBox — And What We're Really Building
          </h1>

          <div className="space-y-6 text-zinc-600 leading-relaxed text-lg">
            <p>
              I still remember the calls. An HR head somewhere in Tamil Nadu, phone
              pressed to ear at nine at night, trying to explain why a single missed
              PF filing across forty sites had suddenly become her problem to fix
              before morning. Multiply that by every EPF deadline, every ESI
              mismatch, every half-yearly return under CLRA and you start to
              understand why so many good businesses spend their nights firefighting
              instead of growing.
            </p>

            <p>
              That's the world I've spent most of my working life in — compliance
              management, legal groundwork and more recently, the technology stacked
              on top of both. I didn't set out to build a "tech company." I set out
              to fix a problem I had watched wear down good people for years.
            </p>

            <p>
              2026 has made that problem sharper, not smaller. India's four Labour
              Codes are finally moving from paper to practice. The central rules were
              notified in May, states are catching up at their own pace, and almost
              every employer I speak with is asking some version of the same
              question: <strong>what actually changes for us, and when?</strong> It's
              a fair question — and it's exactly the kind of moment where businesses
              either get buried chasing thousands of scattered obligations, or get
              ahead of it with the right systems already in place.
            </p>
          </div>

          {/* STATS */}
          <div className="my-12 grid sm:grid-cols-3 gap-6">
            {stats.map((s, i) => (
              <div
                key={i}
                className="border border-zinc-200 rounded-2xl p-6 hover:shadow-xl transition"
              >
                <div className="text-3xl font-bold text-[#d97925] mb-1">
                  {s.value}
                </div>
                <p className="text-zinc-600 text-sm leading-relaxed">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="space-y-6 text-zinc-600 leading-relaxed text-lg">
            <p>
              In the businesses we already work with, that "thousands of scattered
              obligations" isn't a figure of speech — most of it invisible until the
              one thing that gets missed.
            </p>

            <p className="text-2xl font-semibold text-zinc-900">
              That's why we started TickBox Technologies.
            </p>

            <p>
              Our mission is simple to say and harder to live up to every single day:
              build technology-driven solutions that make compliance{" "}
              <strong>genuinely simpler</strong> for the people responsible for it —
              not simpler in a brochure, simpler in practice. Our vision is to become
              the compliance partner businesses trust the most, not because we say
              the right things, but because we show up when it matters.
            </p>

            <p>
              None of that works without values we hold onto even when it's
              inconvenient.
            </p>
          </div>

          {/* VALUES */}
          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            {values.map((val, i) => (
              <div
                key={i}
                className="border border-zinc-200 rounded-2xl p-6 hover:shadow-xl transition"
              >
                <div className="text-3xl mb-3">{val.icon}</div>
                <h4 className="text-xl font-semibold mb-2">{val.name}</h4>
                <p className="text-zinc-600 text-sm leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 space-y-6 text-zinc-600 leading-relaxed text-lg">
            <p>
              Those aren't words on a slide for me. They're the reason our products
              exist.
            </p>
          </div>

          {/* PRODUCTS */}
          <div className="mt-10 space-y-8">
            {products.map((item, i) => (
              <div key={i} className="flex gap-5 items-start">
                <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-[#d97925] text-white font-bold">
                  {item.num}
                </div>
                <div>
                  <h4 className="text-xl font-semibold">{item.title}</h4>
                  <p className="text-zinc-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 space-y-6 text-zinc-600 leading-relaxed text-lg">
            <p>
              We built both because I was tired of watching capable teams treated as
              if the only fix for compliance was more headcount.
            </p>

            <p>
              Here's what I actually want for our clients — whether you're a small
              business filing your first EPF return, or a large corporate managing
              compliance across a dozen states. I want you to stop treating
              compliance as a threat sitting in the corner of your business. I want
              your best people spending their time on the work only they can do,
              while we take care of the deadlines, the exceptions, and the paperwork
              that used to eat their evenings.
            </p>

            <p>
              We're still early. I won't pretend otherwise. But we're building with
              the same discipline we ask our clients to trust us with —{" "}
              <strong>simple, secure, seamless, and accountable</strong> to the
              person on the other end of every filing, every single time.
            </p>
          </div>

          <p className="mt-10 border-l-4 border-[#d97925] pl-5 text-xl font-semibold text-zinc-900">
            — Founder, TickBox Technologies
          </p>
        </div>
      </article>

      <section className="py-28 bg-gradient-to-br from-[#111111] to-[#111111] text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="text-5xl font-bold tracking-tight mb-8"
          >
            Built on Trust. Driven by Care.
          </motion.h2>

          <p className="text-2xl mb-12 max-w-3xl mx-auto opacity-90">
            Committed to delivering clear advice and dependable support.
          </p>

          <motion.a
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            href="/contact"
            className="inline-flex items-center gap-4 px-12 py-7 bg-white text-[#d97925] font-bold text-2xl rounded-3xl shadow-2xl hover:bg-emerald-50 transition-colors"
          >
            Get Started with TickBox
            <ArrowRight size={32} />
          </motion.a>
        </div>
      </section>
    </>
  );
};

export default Articles